import { useState, useEffect } from 'react'
import { X, Plus, Trash2, Mail, Calendar } from 'lucide-react'
import { useAuth } from '../../hooks/useAuth'
import type { Model } from '../../types/models'

interface Permission {
  id: number
  userEmail: string
  permissionType: 'view' | 'download' | 'edit'
  expiresAt?: string
  createdAt: string
}

interface PermissionsModalProps {
  model: Model
  isOpen: boolean
  onClose: () => void
}

export default function PermissionsModal({ model, isOpen, onClose }: PermissionsModalProps) {
  const { token, adminEmail } = useAuth()
  const [permissions, setPermissions] = useState<Permission[]>([])
  const [loading, setLoading] = useState(false)
  const [newPermission, setNewPermission] = useState({
    email: '',
    type: 'view' as 'view' | 'download' | 'edit',
    expiresAt: ''
  })

  useEffect(() => {
    if (isOpen) {
      fetchPermissions()
    }
  }, [isOpen])

  const fetchPermissions = async () => {
    setLoading(true)
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/v2/admin/models/${model.id}/permissions`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Admin-Email': adminEmail!
        }
      })
      if (response.ok) {
        const data = await response.json()
        setPermissions(data.data)
      }
    } catch (error) {
      console.error('Error fetching permissions:', error)
    } finally {
      setLoading(false)
    }
  }

  const addPermission = async () => {
    if (!newPermission.email) return

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/v2/admin/models/${model.id}/permissions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-Admin-Email': adminEmail!
        },
        body: JSON.stringify({
          userEmail: newPermission.email,
          permissionType: newPermission.type,
          expiresAt: newPermission.expiresAt || null
        })
      })

      if (response.ok) {
        await fetchPermissions()
        setNewPermission({ email: '', type: 'view', expiresAt: '' })
      }
    } catch (error) {
      console.error('Error adding permission:', error)
    }
  }

  const removePermission = async (permissionId: number) => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/v2/admin/models/${model.id}/permissions/${permissionId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Admin-Email': adminEmail!
        }
      })

      if (response.ok) {
        await fetchPermissions()
      }
    } catch (error) {
      console.error('Error removing permission:', error)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        <div className="p-6 border-b">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">
              Manage Permissions - {model.name}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="p-6">
          {/* Add new permission */}
          <div className="mb-6">
            <h3 className="text-sm font-medium text-gray-700 mb-3">Add Permission</h3>
            <div className="flex gap-3">
              <input
                type="email"
                placeholder="user@example.com"
                value={newPermission.email}
                onChange={(e) => setNewPermission({ ...newPermission, email: e.target.value })}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              />
              <select
                value={newPermission.type}
                onChange={(e) => setNewPermission({ ...newPermission, type: e.target.value as any })}
                className="px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="view">View</option>
                <option value="download">Download</option>
                <option value="edit">Edit</option>
              </select>
              <input
                type="datetime-local"
                value={newPermission.expiresAt}
                onChange={(e) => setNewPermission({ ...newPermission, expiresAt: e.target.value })}
                placeholder="Expires at (optional)"
                className="px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              />
              <button
                onClick={addPermission}
                disabled={!newPermission.email}
                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Plus className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Permissions list */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-3">Current Permissions</h3>
            {loading ? (
              <p className="text-center text-gray-500 py-8">Loading permissions...</p>
            ) : permissions.length === 0 ? (
              <p className="text-center text-gray-500 py-8">No permissions set</p>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {permissions.map((permission) => (
                  <div
                    key={permission.id}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <div className="flex items-center gap-4">
                      <Mail className="w-4 h-4 text-gray-400" />
                      <span className="font-medium">{permission.userEmail}</span>
                      <span className="px-2 py-1 text-xs bg-white rounded-full capitalize">
                        {permission.permissionType}
                      </span>
                    </div>
                    <div className="flex items-center gap-4">
                      {permission.expiresAt && (
                        <div className="flex items-center gap-1 text-sm text-gray-500">
                          <Calendar className="w-4 h-4" />
                          {new Date(permission.expiresAt).toLocaleDateString()}
                        </div>
                      )}
                      <button
                        onClick={() => removePermission(permission.id)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}